import React, { useEffect, useRef } from 'react';
import './Background.css';

export default function Background() {
  const c0Ref = useRef(null);
  const c1Ref = useRef(null);
  const mouseRef = useRef({ x: window.innerWidth / 2, y: window.innerHeight / 2 });

  useEffect(() => {
    const c0 = c0Ref.current, c1 = c1Ref.current;
    if (!c0 || !c1) return;
    const x0 = c0.getContext('2d'), x1 = c1.getContext('2d');
    let W = c0.width = c1.width = window.innerWidth;
    let H = c0.height = c1.height = window.innerHeight;
    let cx = W / 2, cy = H / 2;
    let running = true;

    const handleResize = () => {
      W = c0.width = c1.width = window.innerWidth;
      H = c0.height = c1.height = window.innerHeight;
      cx = W / 2; cy = H / 2;
    };
    window.addEventListener('resize', handleResize);

    const handleMouse = (e) => {
      const t = e.touches ? e.touches[0] : e;
      mouseRef.current = { x: t.clientX, y: t.clientY };
    };
    window.addEventListener('mousemove', handleMouse);
    window.addEventListener('touchmove', handleMouse, { passive: true });

    // Stars
    const SN = Math.min(280, Math.floor(W * H / 5000));
    const stars = [];
    for (let i = 0; i < SN; i++) stars.push({
      x: Math.random() * W, y: Math.random() * H,
      r: Math.random() * 1.5 + 0.1,
      vx: (Math.random() - 0.5) * 0.06,
      vy: (Math.random() - 0.5) * 0.06,
      ba: Math.random() * 0.45 + 0.05,
      ts: Math.random() * 0.012 + 0.002,
      tp: Math.random() * 6.28,
      hue: [0, 0, 340, 45, 30, 15, 0, 45][Math.floor(Math.random() * 8)]
    });

    let T = 0;

    function drawSpace(t) {
      x0.clearRect(0, 0, W, H);
      // BNFT — dark maroon radial background
      const bg = x0.createRadialGradient(cx, cy * 0.5, 0, cx, cy * 0.5, Math.max(W, H) * 0.85);
      bg.addColorStop(0, '#3a0a14'); bg.addColorStop(0.25, '#260712');
      bg.addColorStop(0.55, '#16040a'); bg.addColorStop(0.85, '#0a0205');
      bg.addColorStop(1, '#050102');
      x0.fillStyle = bg; x0.fillRect(0, 0, W, H);

      // Nebulae — maroon + gold ember clouds
      const nebs = [
        { x: cx * 0.2, y: cy * 0.4, r: 450, c: '165,42,58',  a: 0.045, s: 0.00012, o: 0 },
        { x: cx * 1.7, y: cy * 0.3, r: 380, c: '122,21,48',  a: 0.038, s: 0.0001,  o: 2 },
        { x: cx * 0.5, y: cy * 1.8, r: 420, c: '184,134,11', a: 0.022, s: 0.00014, o: 4 },
        { x: cx * 1.4, y: cy * 1.5, r: 320, c: '90,12,28',   a: 0.030, s: 0.00018, o: 6 },
        { x: cx,       y: cy * 0.15,r: 550, c: '74,14,26',   a: 0.025, s: 0.00007, o: 1 },
        { x: cx * 0.1, y: cy * 1.2, r: 300, c: '251,191,36', a: 0.014, s: 0.0002,  o: 3 },
        { x: cx * 1.8, y: cy * 1.1, r: 260, c: '165,42,58',  a: 0.022, s: 0.00016, o: 5 },
      ];
      nebs.forEach(n => {
        const nx = n.x + Math.sin(t * n.s + n.o) * 110;
        const ny = n.y + Math.cos(t * n.s * 0.7 + n.o) * 80;
        const g = x0.createRadialGradient(nx, ny, 0, nx, ny, n.r);
        g.addColorStop(0, `rgba(${n.c},${n.a})`);
        g.addColorStop(0.5, `rgba(${n.c},${n.a * 0.35})`);
        g.addColorStop(1, 'transparent');
        x0.fillStyle = g; x0.fillRect(nx - n.r, ny - n.r, n.r * 2, n.r * 2);
      });

      // Aurora
      x0.save(); x0.globalAlpha = 0.018; x0.globalCompositeOperation = 'screen';
      for (let i = 0; i < 4; i++) {
        x0.beginPath();
        for (let j = 0; j <= W; j += 3) {
          const y = cy * 0.4 + Math.sin(j * 0.003 + t * 0.0004 + i * 1.8) * 90
            + Math.sin(j * 0.007 - t * 0.0003 + i) * 45
            + Math.cos(j * 0.005 + t * 0.0005 + i * 0.7) * 35;
          j === 0 ? x0.moveTo(j, y) : x0.lineTo(j, y);
        }
        x0.strokeStyle = ['rgba(165,42,58,0.5)', 'rgba(122,21,48,0.35)', 'rgba(251,191,36,0.22)', 'rgba(253,230,138,0.12)'][i];
        x0.lineWidth = [2.5, 2, 1.5, 1][i]; x0.stroke();
      }
      x0.restore();

      // Morphing blob
      x0.save(); x0.translate(cx, cy * 0.42);
      x0.beginPath();
      const bR = Math.min(W, H) * 0.095;
      for (let i = 0; i <= 80; i++) {
        const a = (i / 80) * Math.PI * 2;
        const n = Math.sin(a * 3 + t * 0.002) * 16 + Math.sin(a * 5 - t * 0.003) * 11
          + Math.cos(a * 7 + t * 0.0015) * 8 + Math.sin(a * 11 + t * 0.0008) * 4;
        const r = bR + n;
        i === 0 ? x0.moveTo(Math.cos(a) * r, Math.sin(a) * r) : x0.lineTo(Math.cos(a) * r, Math.sin(a) * r);
      }
      x0.closePath();
      const bG = x0.createRadialGradient(0, 0, 0, 0, 0, bR * 1.7);
      bG.addColorStop(0, 'rgba(165,42,58,0.05)'); bG.addColorStop(0.4, 'rgba(165,42,58,0.025)');
      bG.addColorStop(0.7, 'rgba(122,21,48,0.015)'); bG.addColorStop(1, 'transparent');
      x0.fillStyle = bG; x0.fill();
      x0.strokeStyle = 'rgba(165,42,58,0.025)'; x0.lineWidth = 0.8; x0.stroke();
      x0.restore();
    }

    function drawStars(t) {
      x1.clearRect(0, 0, W, H);
      const mx = mouseRef.current.x, my = mouseRef.current.y;
      for (const s of stars) {
        const dx = mx - s.x, dy = my - s.y, d = Math.sqrt(dx * dx + dy * dy);
        if (d < 220 && d > 0) {
          const f = (220 - d) / 220 * 0.045;
          s.vx -= (dx / d) * f; s.vy -= (dy / d) * f;
        }
        s.vx *= 0.998; s.vy *= 0.998;
        s.x += s.vx; s.y += s.vy;
        const a = s.ba + Math.sin(t * s.ts + s.tp) * 0.2;
        if (s.x < -30) s.x = W + 30; if (s.x > W + 30) s.x = -30;
        if (s.y < -30) s.y = H + 30; if (s.y > H + 30) s.y = -30;
        const g = x1.createRadialGradient(s.x, s.y, 0, s.x, s.y, s.r * 4);
        g.addColorStop(0, `hsla(${s.hue},85%,72%,${Math.max(0, a * 0.4)})`);
        g.addColorStop(1, 'transparent');
        x1.beginPath(); x1.arc(s.x, s.y, s.r * 4, 0, 6.28); x1.fillStyle = g; x1.fill();
        x1.beginPath(); x1.arc(s.x, s.y, s.r, 0, 6.28);
        x1.fillStyle = `hsla(${s.hue},82%,84%,${Math.max(0, a)})`; x1.fill();
      }
      // Connections
      x1.lineWidth = 0.25;
      for (let i = 0; i < stars.length; i++) {
        for (let j = i + 1; j < Math.min(i + 18, stars.length); j++) {
          const dx = stars[i].x - stars[j].x, dy = stars[i].y - stars[j].y;
          if (Math.abs(dx) > 100 || Math.abs(dy) > 100) continue;
          const dd = dx * dx + dy * dy;
          if (dd < 10000) {
            x1.beginPath(); x1.moveTo(stars[i].x, stars[i].y); x1.lineTo(stars[j].x, stars[j].y);
            x1.strokeStyle = `rgba(165,42,58,${0.015 * (1 - Math.sqrt(dd) / 100)})`;
            x1.stroke();
          }
        }
      }
    }

    function loop() {
      if (!running) return;
      T++;
      drawSpace(T);
      drawStars(T);
      requestAnimationFrame(loop);
    }
    loop();

    return () => {
      running = false;
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', handleMouse);
      window.removeEventListener('touchmove', handleMouse);
    };
  }, []);

  return (
    <div className="bg">
      <canvas ref={c0Ref} className="bg__canvas bg__canvas--0" />
      <canvas ref={c1Ref} className="bg__canvas bg__canvas--1" />
      <div className="bg__grain" />
      <div className="bg__vig" />
      <div className="bg__scan bg__scan--1" />
      <div className="bg__scan bg__scan--2" />
      <div className="bg__scan bg__scan--3" />
    </div>
  );
}
